import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, MapPin, ChevronRight, Briefcase, Phone, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edited, setEdited] = useState({});

  useEffect(() => {
    Promise.all([
      api.get(`/clients/${clientId}`).then(r => r.data),
      api.get("/quotes?include_deleted=true").then(r => r.data),
    ]).then(([c, allJobs]) => {
      setClient(c);
      const fn = (c.first_name || "").toLowerCase().trim();
      const ln = (c.last_name || "").toLowerCase().trim();
      const clientJobs = allJobs.filter(j => {
        const jfn = (j.client_first_name || "").toLowerCase().trim();
        const jln = (j.client_last_name || "").toLowerCase().trim();
        return jfn === fn && jln === ln;
      });
      setJobs(clientJobs);
    }).catch(() => toast.error("Failed to load"))
    .finally(() => setLoading(false));
  }, [clientId]);

  const handleSave = async () => {
    if (Object.keys(edited).length === 0) return;
    setSaving(true);
    try {
      const resp = await api.put(`/clients/${clientId}`, edited);
      setClient(resp.data);
      setEdited({});
      toast.success("Client details saved — these are now the defaults for new jobs");
    } catch (e) { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const field = (key) => edited[key] !== undefined ? edited[key] : (client?.[key] || "");
  const setField = (key, val) => setEdited(prev => ({ ...prev, [key]: val }));

  if (loading || !client) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><p style={{ color: "var(--text-muted)" }}>Loading...</p></div>;

  // Group jobs by address
  const byAddr = {};
  jobs.forEach(j => {
    const addr = (j.site_address || "No address").trim();
    if (!byAddr[addr]) byAddr[addr] = [];
    byAddr[addr].push(j);
  });

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="px-8 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="btn-secondary h-9">
              <ArrowLeft className="w-4 h-4 mr-1" /> Clients
            </Button>
            <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>{client.first_name} {client.last_name}</h1>
            <span className="badge-pill badge-blue text-xs">{client.client_type}</span>
          </div>
          {Object.keys(edited).length > 0 && (
            <Button onClick={handleSave} disabled={saving} className="btn-primary gap-2 h-10 px-5">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-4">
        {/* Client Details — Editable */}
        <section className="ui-surface p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>
              <User className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Client Details
            </h2>
            <p style={{ fontSize: "11px", fontStyle: "italic", color: "var(--text-muted)" }}>Changes here set the default for new jobs</p>
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-3">
            <div>
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>First Name</Label>
              <Input value={field("first_name")} onChange={(e) => setField("first_name", e.target.value)} className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} />
            </div>
            <div>
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>Last Name</Label>
              <Input value={field("last_name")} onChange={(e) => setField("last_name", e.target.value)} className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} />
            </div>
            <div>
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>
                <Mail className="w-3 h-3 inline mr-1" /> Email
              </Label>
              <Input value={field("email")} onChange={(e) => setField("email", e.target.value)} className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} />
            </div>
            <div>
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>
                <Phone className="w-3 h-3 inline mr-1" /> Phone
              </Label>
              <Input value={field("phone")} onChange={(e) => setField("phone", e.target.value)} className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] font-medium tracking-wide uppercase mb-1.5 block" style={{ color: "var(--text-muted)" }}>Client Type</Label>
              <Input value={field("client_type")} disabled className="h-10 rounded-lg bg-slate-50" style={{ border: "1px solid var(--border)" }} />
            </div>
          </div>
        </section>

        {/* Jobs */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
            <Briefcase className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Jobs ({jobs.length})
          </h2>

          {jobs.length === 0 ? (
            <div className="ui-surface p-12 text-center" style={{ color: "var(--text-muted)" }}>No jobs for this client</div>
          ) : (
            <div className="space-y-5">
              {Object.entries(byAddr).map(([addr, addrJobs]) => (
                <div key={addr}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: addrJobs.length > 1 ? "#FEF3C7" : "var(--surface)", border: `1px solid ${addrJobs.length > 1 ? "#F59E0B" : "var(--border)"}` }}>
                      <MapPin className="w-3.5 h-3.5" style={{ color: addrJobs.length > 1 ? "#D97706" : "var(--text-muted)" }} />
                    </div>
                    <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{addr}</span>
                    {addrJobs.length > 1 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>{addrJobs.length} jobs</span>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-9">
                    {addrJobs.map(j => (
                      <div key={j.id} onClick={() => navigate(`/quotes/${j.id}`)} className="ui-surface p-4 cursor-pointer hover:shadow-md transition-shadow group">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-mono font-bold" style={{ color: "var(--primary)" }}>{j.quote_number || "—"}</span>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full status-${(j.status || "not-started").toLowerCase().replace(/\s+/g, "-")}`}>{j.status}</span>
                              {j.works_type === "Works Order" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>WO</span>}
                              {j.is_deleted && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#6B7280" }}>Archived</span>}
                            </div>
                            <p className="text-sm font-medium mt-1" style={{ color: "var(--text-primary)" }}>
                              {j.actions?.[0]?.title || j.job_details?.slice(0, 50) || j.works_type || "Job"}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--text-muted)" }} />
                        </div>
                        <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
                          {j.start_date && <span>{j.start_date}</span>}
                          {j.total_inc_vat > 0 && <span className="font-mono font-semibold" style={{ color: "var(--text-primary)" }}>{formatCurrency(j.total_inc_vat)}</span>}
                          {j.booked_team_member_name && <span>{j.booked_team_member_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
